import React, { useState } from 'react';
import { Modal } from './Modal';
import Button from './ui/Button';
import { FIELD } from './ui/tokens';

/**
 * View/edit matrix aligned with the app sidebar + backend `src/config/permissions.js` screen keys.
 */
const ROLE_MATRIX_SCREENS = [
  { key: 'dashboard', label: 'Home' },
  { key: 'quotations', label: 'Inbox & Quote Generator' },
  { key: 'confirmed_orders', label: 'Confirmed Orders' },
  { key: 'vendor_purchase_orders', label: 'Vendor Purchase Orders' },
  { key: 'invoices', label: 'Client & Vendor Invoices' },
  { key: 'products', label: 'Price Lists & Master Catalog' },
  { key: 'inventory', label: 'Inventory Management' },
  { key: 'financials', label: 'Accounts' },
  { key: 'reports', label: 'Reports' },
  { key: 'users', label: 'User Management' },
  { key: 'templates', label: 'Template Management' },
  { key: 'vendors', label: 'Vendor List' },
  { key: 'clients', label: 'Client List' },
  { key: 'settings', label: 'Settings' },
  { key: 'orders', label: 'Orders' },
  { key: 'insights', label: 'Insights' },
  { key: 'role_management', label: 'Role Management' },
  { key: 'audit_logs', label: 'Audit Logs' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'price_list', label: 'Vendor Price List' },
  { key: 'price_list_settings', label: 'Price List Settings' },
  { key: 'portal_automation', label: 'Portal Automation' }
] as const;

interface AddRoleModalProps {
  onClose: () => void;
  onSubmit: (roleData: any) => void;
  initialData?: any;
}

interface PermissionState {
  [screen: string]: {
    view: boolean;
    edit: boolean;
  };
}

function buildDefaultPermissions(): PermissionState {
  const initialPermissions: PermissionState = {};
  ROLE_MATRIX_SCREENS.forEach(({ key }) => {
    initialPermissions[key] = {
      view: key === 'dashboard',
      edit: false
    };
  });
  return initialPermissions;
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({ onClose, onSubmit, initialData }) => {
  const isReadOnly = initialData?.isSystemRole;
  const [roleName, setRoleName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [permissions, setPermissions] = useState<PermissionState>(() => {
    const base = buildDefaultPermissions();
    if (initialData?.permissions) {
      const raw = initialData.permissions as Record<string, { view?: boolean; edit?: boolean }>;
      ROLE_MATRIX_SCREENS.forEach(({ key }) => {
        const existing = raw[key];
        base[key] = {
          view: existing?.view ?? (key === 'dashboard'),
          edit: existing?.edit ?? false
        };
      });
    }
    return base;
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!roleName.trim()) {
      newErrors.roleName = 'Role name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckboxChange = (screenKey: string, permissionType: 'view' | 'edit') => {
    if (screenKey === 'dashboard' && permissionType === 'view') {
      return;
    }

    setPermissions((prev) => {
      const newPermissions = { ...prev };

      if (permissionType === 'edit') {
        if (!newPermissions[screenKey].edit) {
          newPermissions[screenKey] = {
            view: true,
            edit: true
          };
        } else {
          newPermissions[screenKey] = {
            ...newPermissions[screenKey],
            edit: false
          };
        }
      } else {
        if (newPermissions[screenKey].view) {
          newPermissions[screenKey] = {
            view: false,
            edit: false
          };
        } else {
          newPermissions[screenKey] = {
            ...newPermissions[screenKey],
            view: true
          };
        }
      }

      return newPermissions;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      const formattedPermissions: {
        [screen: string]: {
          view: boolean;
          edit: boolean;
          create: boolean;
          delete: boolean;
          export: boolean;
          import: boolean;
        };
      } = {};
      Object.entries(permissions).forEach(([screen, perms]) => {
        formattedPermissions[screen] = {
          view: perms.view,
          edit: perms.edit,
          create: perms.edit,
          delete: perms.edit,
          export: perms.view,
          import: perms.edit
        };
      });

      const roleData = {
        name: roleName.trim().toLowerCase().replace(/\s+/g, '_'),
        displayName: roleName.trim(),
        description: description.trim(),
        permissions: formattedPermissions
      };

      onSubmit(roleData);
    }
  };

  // The app's Modal, Button and data-table, the same as the user form beside it. This was its
  // own overlay in the retired `neutral-*` palette, with all-caps buttons and a permission
  // grid drawn from divs.
  const checkboxClass = 'h-4 w-4 cursor-pointer accent-primary-500 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <Modal isOpen title={initialData ? 'Edit role' : 'Add new role'} onClose={onClose} maxWidthClass="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="role-name" className="mb-1 block text-sm font-medium text-slate-700">
            Role name
          </label>
          <input
            id="role-name"
            type="text"
            value={roleName}
            onChange={(e) => {
              setRoleName(e.target.value);
              if (errors.roleName) {
                setErrors((prev) => ({ ...prev, roleName: '' }));
              }
            }}
            disabled={isReadOnly}
            className={`${FIELD} h-10 disabled:cursor-not-allowed disabled:opacity-50 ${
              errors.roleName ? 'border-red-300' : 'border-slate-200'
            }`}
            placeholder="Enter role name"
          />
          {errors.roleName && <p className="mt-1 text-xs text-red-600">{errors.roleName}</p>}
        </div>

        <div>
          <label htmlFor="role-description" className="mb-1 block text-sm font-medium text-slate-700">
            Description <span className="font-normal text-slate-500">(optional)</span>
          </label>
          <textarea
            id="role-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            disabled={isReadOnly}
            className={`${FIELD} resize-none border-slate-200 py-2 disabled:cursor-not-allowed disabled:opacity-50`}
            placeholder="Enter description"
          />
        </div>

        <div>
          <p className="mb-1 text-sm font-medium text-slate-700">Permissions</p>
          <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
            <table className="data-table data-table-dense">
              <thead>
                <tr>
                  <th scope="col" className="sticky top-0 z-10">Module</th>
                  <th scope="col" className="sticky top-0 z-10 w-20 text-center">View</th>
                  <th scope="col" className="sticky top-0 z-10 w-20 text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {ROLE_MATRIX_SCREENS.map((screen) => (
                  <tr key={screen.key}>
                    <td>
                      {screen.label}
                      {screen.key === 'dashboard' && <span className="ml-2 text-xs text-slate-500">(required)</span>}
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        aria-label={`${screen.label}: view`}
                        checked={permissions[screen.key]?.view || false}
                        onChange={() => handleCheckboxChange(screen.key, 'view')}
                        disabled={screen.key === 'dashboard' || isReadOnly}
                        className={checkboxClass}
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        aria-label={`${screen.label}: edit`}
                        checked={permissions[screen.key]?.edit || false}
                        onChange={() => handleCheckboxChange(screen.key, 'edit')}
                        disabled={isReadOnly}
                        className={checkboxClass}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          {isReadOnly ? (
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="primary">
                {initialData ? 'Update role' : 'Create role'}
              </Button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddRoleModal;
